import {
  TextInput,
  Textarea,
  Stack,
  Switch,
  Select,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Source } from "../../../services/admin/sources.service";
import { useCollectionMethodsAdmin } from "../../../hooks/admin/useCollectionMethods";
import { required, scrollToFirstError } from "../../../utils/formValidation";

export interface SourceFormValues {
  name: string;
  description: string;
  collectionMethodId: string;
  isActive: boolean;
}

interface SourceFormProps {
  initialValues?: Source;
  onSubmit: (values: SourceFormValues) => void;
}

export default function SourceForm({
  initialValues,
  onSubmit,
}: SourceFormProps) {
  const { data: collectionMethods = [] } = useCollectionMethodsAdmin();

  const form = useForm<SourceFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      collectionMethodId: initialValues?.collectionMethodId || "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      name: required("Source name is required"),
    },
  });

  const collectionMethodOptions = collectionMethods
    .filter((cm) => cm.isActive !== false)
    .map((c) => ({ value: c.id, label: c.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <form
      noValidate
      onSubmit={form.onSubmit(onSubmit, () => scrollToFirstError(form))}
    >
      <Stack gap="md">
        <TextInput
          label="Source Name"
          placeholder="e.g., Breast, Colon, Skin"
          withAsterisk
          {...form.getInputProps("name")}
        />

        <Textarea
          label="Description"
          placeholder="Enter source description"
          minRows={3}
          {...form.getInputProps("description")}
        />

        <Select
          label="Default Collection Method"
          placeholder="Select collection method"
          data={collectionMethodOptions}
          searchable
          clearable
          {...form.getInputProps("collectionMethodId")}
        />

        {/* Organ Map Template - Read-only (only shown in edit mode) */}
        {initialValues && (
          <div>
            <Text size="sm" fw={500} mb={4}>
              Organ Map Template
            </Text>
            <Text
              size="sm"
              c={initialValues.organMapTemplate?.name ? undefined : "dimmed"}
            >
              {initialValues.organMapTemplate?.name || "Not linked"}
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              Managed from Organ Map Templates page
            </Text>
          </div>
        )}

        <Switch
          label="Active"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
